import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../contexts/ToastContext";
import type { Tenant } from "../../services/tenantApi";
import { updateTenantModules } from "../../services/tenantApi";
import type { ModuleName } from "../../lib/modules";
import { ModuleSelector } from "./ModuleSelector";
import { TENANT_KEYS } from "../../hooks/useTenants";

/**
 * Props for ModuleManager.
 * @param tenant - Current tenant whose modules are managed.
 */
interface ModuleManagerProps {
  tenant: Tenant;
}

/**
 * Manages tenant module toggles using optimistic updates (react-query onMutate/onError/onSettled).
 * Uses a dedicated useMutation with optimistic logic baked in — required for react-query v5
 * where onMutate/onError/onSettled cannot be passed to mutate() call directly.
 */
export const ModuleManager: React.FC<ModuleManagerProps> = ({ tenant }) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  // Dedicated optimistic mutation for module toggling (B3)
  const updateModulesMutation = useMutation({
    mutationFn: ({
      tenantId,
      enabledModules,
    }: {
      tenantId: string;
      enabledModules: string[];
    }) => updateTenantModules(tenantId, enabledModules),

    // B3: Optimistic update — snapshot, update cache immediately
    onMutate: async ({ tenantId, enabledModules }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: TENANT_KEYS.detail(tenantId) });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<Tenant>(
        TENANT_KEYS.detail(tenantId)
      );

      // Optimistically update the cache
      queryClient.setQueryData<Tenant>(
        TENANT_KEYS.detail(tenantId),
        (old) => {
          if (!old) return old;
          return { ...old, enabledModules };
        }
      );

      return { previousData };
    },

    // B3: Rollback on error
    onError: (_err, { tenantId }, context) => {
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(TENANT_KEYS.detail(tenantId), context.previousData);
      }
      addToast({
        message: "Błąd aktualizacji modułów. Przywrócono poprzedni stan.",
        type: "error",
      });
    },

    // B3: Always invalidate to sync with server
    onSettled: (_data, _err, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: TENANT_KEYS.detail(tenantId) });
    },
  });

  const handleModuleToggle = (module: ModuleName, isEnabled: boolean): void => {
    const currentModules: string[] = tenant.enabledModules || [];
    const newModules = isEnabled
      ? [...currentModules, module]
      : currentModules.filter((m) => m !== module);

    updateModulesMutation.mutate({ tenantId: tenant.id, enabledModules: newModules });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-medium text-neutral-900">
          Włączone moduły
        </h4>
        {updateModulesMutation.isPending && (
          <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-neutral-400 border-t-transparent" aria-label="Zapisywanie..." />
        )}
      </div>
      <ModuleSelector
        value={tenant.enabledModules || []}
        onChange={handleModuleToggle}
        disabled={updateModulesMutation.isPending}
      />
    </div>
  );
};

export default ModuleManager;
